(() => {
  'use strict';
  if (window.GutSuppCartGifts) return;

  const SOURCE = 'gutsupp-subscription-gifts';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const MAX_RECONCILE_ATTEMPTS = 3;
  let queue = Promise.resolve();
  let pending = 0;
  let lastError = null;
  let lastCart = null;
  let hooks = {};

  function config() {
    const element = document.getElementById('gutsupp-cart-gift-config');
    if (!element) return { enabled: false };
    return JSON.parse(element.textContent || '{}');
  }

  function notify(name, value) {
    if (typeof hooks[name] !== 'function') return;
    try { hooks[name](value); } catch (_) {}
  }

  function setError(error) {
    lastError = error;
    notify('onError', error);
  }

  function cartError(message, retryable = false, status) {
    const error = new Error(message);
    error.retryable = retryable;
    if (status !== undefined) error.status = status;
    return error;
  }

  // A mutation is sent once. Recovery always starts by reading the cart again:
  // its response may have been lost after Shopify committed the change.
  async function request(path, body, settings, format = 'json') {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root + path, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: format === 'text' ? 'text/html' : 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: controller.signal
      });
      let data;
      try { data = format === 'text' ? await response.text() : await response.json(); }
      catch (_) {
        throw cartError(settings.error || 'Could not read your cart. Please try again.', response.ok || response.status === 429 || response.status >= 500, response.status);
      }
      if (!response.ok) {
        throw cartError((data && (data.description || data.message)) || settings.error || 'Could not update your cart. Please try again.', response.status === 429 || response.status >= 500, response.status);
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw cartError(settings.error || 'Your cart took too long to update. Please try again.', true);
      if (error.retryable === undefined && error instanceof TypeError) {
        throw cartError(settings.error || 'Could not reach your cart. Please try again.', true);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function isCartSnapshot(cart) {
    return !!cart && Array.isArray(cart.items) && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0;
  }

  function itemsOf(cart, settings) {
    if (!cart || !Array.isArray(cart.items)) throw cartError(settings.error || 'Could not read your cart. Please try again.', true);
    return cart.items;
  }

  function variantOf(item) { return String(item.variant_id || item.id || ''); }
  function planOf(item) {
    const allocation = item.selling_plan_allocation;
    return String((allocation && ((allocation.selling_plan && allocation.selling_plan.id) || allocation.selling_plan_id)) || item.selling_plan || '');
  }
  function marked(item) { return item.properties && item.properties._gutsupp_gift_source === SOURCE; }
  function sameCode(left, right) { return String(left).toUpperCase() === String(right).toUpperCase(); }
  function isZeroPrice(value) {
    return value !== null && value !== undefined && value !== '' && Number.isSafeInteger(Number(value)) && Number(value) === 0;
  }

  function giftProperties(parent) {
    return {
      _gutsupp_gift: 'true',
      _gutsupp_gift_source: SOURCE,
      _gutsupp_parent_variant_id: String(parent.variant),
      _gutsupp_parent_selling_plan_id: String(parent.plan)
    };
  }

  function winner(cart, settings) {
    const tiers = Array.isArray(settings.tiers) ? settings.tiers : [];
    return tiers.map(tier => {
      const parent = itemsOf(cart, settings).find(item =>
        !marked(item) && Number(item.quantity) > 0 && variantOf(item) === String(tier.variant) &&
        (planOf(item) === String(tier.plan) || (tier.allowOneTime === true && !planOf(item)))
      );
      return parent ? { ...tier, plan: planOf(parent) } : null;
    }).filter(Boolean).sort((left, right) => Number(right.days) - Number(left.days))[0] || null;
  }

  function desiredGifts(parent, settings) {
    if (!parent) return [];
    const gifts = Number(parent.days) >= 180 ? [settings.mug, settings.whisk] : [settings.mug];
    if (gifts.some(id => !Number.isSafeInteger(Number(id)) || Number(id) <= 0) || new Set(gifts.map(String)).size !== gifts.length) {
      throw cartError(settings.error || 'The gift offer could not be prepared. Please try again.');
    }
    return gifts.map(String);
  }

  function currentCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes.map(discount => typeof discount === 'string' ? discount : discount.code).filter(code => typeof code === 'string' && code.trim()).map(code => code.trim());
  }

  function managedCodes(settings) {
    return ['STARTER90', 'STARTER180', settings.mugCode, settings.whiskCode].filter(Boolean);
  }

  function desiredCodes(cart, parent, settings) {
    const offerCodes = managedCodes(settings);
    const codes = currentCodes(cart).filter(code => !offerCodes.some(offer => sameCode(code, offer)));
    if (parent) {
      const code = Number(parent.days) >= 180 ? settings.whiskCode : settings.mugCode;
      if (typeof code !== 'string' || !code.trim()) throw cartError(settings.error || 'The gift offer could not be applied. Please try again.');
      codes.push(code.trim());
    }
    return codes.filter((code, index) => codes.findIndex(other => sameCode(code, other)) === index);
  }

  function linkedCorrectly(item, parent) {
    const properties = item.properties || {};
    return !planOf(item) && properties._gutsupp_gift === 'true' &&
      String(properties._gutsupp_parent_variant_id || '') === String(parent.variant) &&
      String(properties._gutsupp_parent_selling_plan_id || '') === String(parent.plan);
  }

  function verify(cart, settings) {
    const parent = winner(cart, settings);
    const wanted = desiredGifts(parent, settings);
    const gifts = itemsOf(cart, settings).filter(marked);
    if (gifts.length !== wanted.length || wanted.some(id => gifts.filter(item => variantOf(item) === id).length !== 1)) {
      throw cartError(settings.error || 'The gift offer could not be prepared. Please try again.', true);
    }
    if (parent && gifts.some(item => Number(item.quantity) !== 1 || !linkedCorrectly(item, parent) || !isZeroPrice(item.final_line_price))) {
      throw cartError(settings.error || 'Your free gifts could not be confirmed. Please try again before checkout.', true);
    }
    // Actual zero-priced, linked gift lines are authoritative. Discount-code
    // metadata can be absent or temporarily report an inapplicable flag.
    return cart;
  }

  async function reconcileAttempt(settings, initialCart) {
    let cart = isCartSnapshot(initialCart) ? initialCart : await request('cart.js', undefined, settings);
    itemsOf(cart, settings);
    if (settings.enabled !== true) return cart;

    let parent = winner(cart, settings);
    let wanted = desiredGifts(parent, settings);
    const kept = new Set();
    const updates = {};
    itemsOf(cart, settings).filter(marked).forEach(item => {
      const id = variantOf(item);
      const keep = parent && wanted.includes(id) && !kept.has(id) && linkedCorrectly(item, parent) && Number(item.quantity) > 0;
      if (keep) {
        kept.add(id);
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    });
    if (Object.keys(updates).length) {
      await request('cart/update.js', { updates }, settings);
      cart = await request('cart.js', undefined, settings);
      parent = winner(cart, settings);
      wanted = desiredGifts(parent, settings);
    }

    const present = new Set(itemsOf(cart, settings).filter(item => marked(item) && parent && linkedCorrectly(item, parent) && Number(item.quantity) === 1).map(variantOf));
    const missing = wanted.filter(id => !present.has(id));
    if (missing.length) {
      await request('cart/add.js', { items: missing.map(id => ({ id: Number(id), quantity: 1, properties: giftProperties(parent) })) }, settings);
      cart = await request('cart.js', undefined, settings);
      parent = winner(cart, settings);
    }

    const existing = currentCodes(cart);
    const codes = desiredCodes(cart, parent, settings);
    const needCodes = codes.length !== existing.length || codes.some(code => !existing.some(other => sameCode(code, other)));
    let verified = false;
    try { verify(cart, settings); verified = true; } catch (error) { if (!error.retryable) throw error; }
    const requiredCode = parent && (Number(parent.days) >= 180 ? settings.whiskCode : settings.mugCode);
    const staleOfferCode = existing.some(code => managedCodes(settings).some(offer => sameCode(code, offer)) && (!requiredCode || !sameCode(code, requiredCode)));
    // Do not rewrite a working zero-priced gift cart just because optional code
    // metadata is missing. Only change managed offer codes; keep other promos.
    if ((!verified && parent) || needCodes && (staleOfferCode || !verified || !parent)) {
      const updated = await request('cart/update.js', { discount: codes.join(',') }, settings);
      if (isCartSnapshot(updated)) {
        try { return verify(updated, settings); }
        catch (error) { if (!error.retryable) throw error; }
      }
      cart = await request('cart.js', undefined, settings);
    }
    return verify(cart, settings);
  }

  async function reconcileInQueue(options = {}) {
    const settings = config();
    let cart;
    for (let attempt = 1; attempt <= MAX_RECONCILE_ATTEMPTS; attempt += 1) {
      try {
        cart = await reconcileAttempt(settings, attempt === 1 ? options.initialCart : undefined);
        break;
      } catch (error) {
        if (!error.retryable || attempt === MAX_RECONCILE_ATTEMPTS) throw error;
        await new Promise(resolve => window.setTimeout(resolve, attempt * 100));
      }
    }
    // Rendering the drawer is optional presentation work, never a checkout gate.
    // A separate GET also avoids an otherwise unnecessary cart mutation.
    if (options.sections !== false && (!cart.sections || !cart.sections['cart-aside'])) {
      try {
        const html = await request('?section_id=cart-aside', undefined, settings, 'text');
        if (typeof html === 'string' && html.trim() && html.includes('cart-aside')) {
          cart.sections = { ...(cart.sections || {}), 'cart-aside': html };
        }
      } catch (_) {}
    }
    lastCart = cart;
    return lastCart;
  }

  function run(task) {
    if (typeof task !== 'function') return Promise.reject(new TypeError('A cart task is required.')) ;
    pending += 1;
    if (pending === 1) notify('onBusy', true);
    const job = queue.then(async () => {
      setError(null);
      try {
        return await task({ reconcile: reconcileInQueue });
      } catch (error) {
        setError(error);
        throw error;
      }
    });
    const finished = job.finally(() => {
      pending -= 1;
      if (pending === 0) notify('onBusy', false);
    });
    queue = finished.catch(() => undefined);
    return finished;
  }

  window.GutSuppCartGifts = Object.freeze({
    run,
    reconcile: options => run(context => context.reconcile(options)),
    ready: async () => { await queue; if (lastError) throw lastError; return lastCart; },
    configureHooks: callbacks => { hooks = callbacks || {}; notify('onBusy', pending > 0); notify('onError', lastError); },
    get config() { return config(); },
    get busy() { return pending > 0; },
    get error() { return lastError; }
  });
})();

